import { useState, useRef, useMemo, useEffect, useCallback } from "react";

interface UseUnsavedChangesOptions {
  /** Current state to compare against the initial snapshot. */
  currentState: unknown;
  /** Initial state (serialized once on mount). */
  initialState: unknown;
  /** Called when the user confirms navigating away via Cancel / Back. */
  onCancel: () => void;
}

interface UseUnsavedChangesResult {
  hasChanges: boolean;
  /**
   * Call after a successful save to reset the snapshot. Pass the saved state
   * when it was just set and the render has not caught up yet.
   */
  markSaved: (savedState?: unknown) => void;
  /** Cancel/Back handler — prompts if unsaved changes exist. */
  handleCancel: () => void;
}

export function useUnsavedChanges({
  currentState,
  initialState,
  onCancel,
}: UseUnsavedChangesOptions): UseUnsavedChangesResult {
  const initialSnapshot = useRef(JSON.stringify(initialState));
  const [saveCount, setSaveCount] = useState(0);

  const hasChanges = useMemo(
    () => JSON.stringify(currentState) !== initialSnapshot.current,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentState, saveCount],
  );

  const markSaved = useCallback((...args: [savedState?: unknown]) => {
    initialSnapshot.current = JSON.stringify(args.length ? args[0] : currentState);
    setSaveCount((c) => c + 1);
  }, [currentState]);

  // Warn on browser close/refresh
  useEffect(() => {
    if (!hasChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasChanges]);

  // Intercept in-app navigation (history.pushState / replaceState / popstate)
  const hasChangesRef = useRef(false);
  const bypassGuardRef = useRef(false);
  hasChangesRef.current = hasChanges;

  useEffect(() => {
    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(window.history);

    const guardNavigation = (
      original: typeof window.history.pushState,
      ...args: Parameters<typeof window.history.pushState>
    ) => {
      if (hasChangesRef.current && !bypassGuardRef.current) {
        const leave = window.confirm(
          "You have unsaved changes. Are you sure you want to leave?",
        );
        if (!leave) return;
        // User confirmed — bypass further prompts until cleanup
        bypassGuardRef.current = true;
      }
      original(...args);
    };

    window.history.pushState = (...args) =>
      guardNavigation(originalPushState, ...args);
    window.history.replaceState = (...args) =>
      guardNavigation(originalReplaceState, ...args);

    const handlePopState = () => {
      if (hasChangesRef.current && !bypassGuardRef.current) {
        const leave = window.confirm(
          "You have unsaved changes. Are you sure you want to leave?",
        );
        if (!leave) {
          window.history.pushState(null, "", window.location.href);
        } else {
          bypassGuardRef.current = true;
        }
      }
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const handleCancel = useCallback(() => {
    if (hasChanges) {
      const leave = window.confirm(
        "You have unsaved changes. Are you sure you want to discard them?",
      );
      if (!leave) return;
    }
    bypassGuardRef.current = true;
    onCancel();
    bypassGuardRef.current = false;
  }, [hasChanges, onCancel]);

  return { hasChanges, markSaved, handleCancel };
}
